import { useCallback, useMemo, useRef, useState } from "react";

import { useTurnstile } from "~/components/Turnstile";
import { useRetryCountdown } from "~/components/VerificationNotice";
import { getAuthGuardError, isPerNumberLimit } from "~/utils/turnstile-error";

import { useFormSignals } from "./useFormSignals";

import type { RefObject } from "react";
import type { UseTurnstileResult } from "~/components/Turnstile";
import type { TurnstileProof } from "~/lib/antibot/turnstile-controller";
import type { AuthSurface, ClientSignals, TurnstileTier } from "~/lib/antibot/types";
import type { AuthGuardErrorInfo } from "~/utils/turnstile-error";

/**
 * El antibot de un formulario que pide un código por WhatsApp, entero: la constancia de
 * Turnstile (dos niveles), las señales del formulario, el campo trampa y qué hacer con cada
 * respuesta de la API. Los cinco formularios (login, registro, inscripción directa, checkout y
 * cambio de teléfono) lo usan igual, también en "Reenviar código".
 *
 *   const guard = useAuthGuard("login");
 *   const result = await guard.send((antibot) => sendOTP({ phone, context: "login", ...antibot }));
 *
 * `send` espera la constancia (el botón dice "Verificando…"), arma el body del antibot y llama al
 * pedido. Si la API contesta `TURNSTILE_STEP_UP`, monta el nivel con clic y REINTENTA SOLO con
 * ese token, sin que la persona toque nada más que la casilla (si Cloudflare la pide). Si contesta
 * `TURNSTILE_EXPIRED`, pide un token nuevo y reintenta una vez. Lo demás del antibot (límites,
 * bloqueo, los rechazos de siempre) queda como aviso en el formulario, con su cuenta regresiva.
 *
 * Importante: el pedido lee los datos del formulario AL MOMENTO de mandarse (no al tocar el
 * botón), porque entre medio puede haber una casilla de por medio.
 */

/** Lo que el antibot suma al body de send-otp. */
export type AuthGuardBody = {
  turnstileToken?: string;
  turnstileTier: TurnstileTier;
  attemptId: string;
  surface: AuthSurface;
  clientSignals: ClientSignals;
};

export type AuthGuardResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      /**
       * - `busy`: ya había un envío en curso (doble toque).
       * - `cooldown`: todavía corre la espera de un rechazo anterior.
       * - `verification`: el nivel con clic no pudo completar (el slot ya lo explica).
       * - `rejected`: la API rechazó por el antibot o un límite (el aviso ya está a la vista).
       * - `error`: cualquier otra falla (el formulario decide: toast, mensaje, etc.).
       */
      reason: "busy" | "cooldown" | "verification" | "rejected" | "error";
      error?: unknown;
    };

export type AuthGuard = {
  surface: AuthSurface;
  turnstile: UseTurnstileResult;
  /** Ref para `<BotTrap />`. */
  trapRef: RefObject<HTMLInputElement | null>;
  send: <T>(request: (body: AuthGuardBody) => Promise<T>) => Promise<AuthGuardResult<T>>;
  /** Hay un envío en curso (esperando constancia o respuesta). */
  busy: boolean;
  /** Esperando la constancia anti-bot: el botón dice "Verificando…". */
  verifying: boolean;
  /** Aviso del último rechazo del antibot o de un límite (null si no hay). */
  notice: AuthGuardErrorInfo | null;
  retrySecondsLeft: number;
  retryTotalSeconds: number;
  /**
   * Avisar que la persona cambió el número. Si el aviso vigente es un límite de ESE número (o de
   * su país), deja de valer y el botón se destraba: la API aceptaría el número nuevo. Los demás
   * (IP, dispositivo, presupuesto, antibot) siguen. Llamarlo sólo cuando el número cambió de
   * verdad, no en cada render.
   */
  phoneChanged: () => void;
};

const CLIENT_ERROR_MAX = 200;

/** Un step-up que no se puede cumplir (no debería pasar): se explica como una falla, no como un pedido de clic. */
const UNSATISFIABLE_STEP_UP: Pick<AuthGuardErrorInfo, "title" | "body" | "retryAfterSeconds"> = {
  title: "No pudimos terminar la comprobación",
  body: "Suele ser la conexión, o una extensión del navegador que la bloquea.",
  retryAfterSeconds: 5,
};

export function useAuthGuard(surface: AuthSurface): AuthGuard {
  const turnstile = useTurnstile({ surface });
  const { controller } = turnstile;
  const signals = useFormSignals();
  const { read: readSignals } = signals;
  const countdown = useRetryCountdown();
  const { start: startCountdown, clear: clearCountdown } = countdown;
  const [notice, setNotice] = useState<AuthGuardErrorInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  // `send` vive en handlers que capturaron un render viejo: la espera vigente se lee por ref.
  const secondsLeftRef = useRef(0);
  secondsLeftRef.current = countdown.secondsLeft;
  const noticeRef = useRef<AuthGuardErrorInfo | null>(null);
  noticeRef.current = notice;

  const buildBody = useCallback(
    (proof: TurnstileProof): AuthGuardBody => {
      const clientSignals: ClientSignals = {
        ...readSignals(),
        waitedMs: Math.max(0, Math.round(proof.waitedMs)),
      };
      if (proof.clientError)
        clientSignals.clientError = proof.clientError.slice(0, CLIENT_ERROR_MAX);
      const body: AuthGuardBody = {
        // Siempre: sin `turnstileTier` la API asume un bundle viejo y nunca pide el clic.
        turnstileTier: proof.tier,
        attemptId: controller.getAttemptId(),
        surface,
        clientSignals,
      };
      if (proof.token) body.turnstileToken = proof.token;
      return body;
    },
    [controller, readSignals, surface],
  );

  const send = useCallback(
    async <T>(request: (body: AuthGuardBody) => Promise<T>): Promise<AuthGuardResult<T>> => {
      if (busyRef.current) return { ok: false, reason: "busy" };
      if (secondsLeftRef.current > 0) return { ok: false, reason: "cooldown" };
      busyRef.current = true;
      setBusy(true);
      setNotice(null);
      clearCountdown();

      try {
        let proof = await controller.getProof();
        let steppedUp = false;
        let refreshed = false;

        for (;;) {
          if (proof.failed) return { ok: false, reason: "verification" };
          try {
            return { ok: true, data: await request(buildBody(proof)) };
          } catch (error) {
            const info = getAuthGuardError(error);

            // La API quiere el nivel con clic: se monta y se reintenta con ese token, mismo
            // intento. Nunca dos veces (la API no pide el clic a un pedido que ya lo trae).
            if (info?.code === "TURNSTILE_STEP_UP" && proof.tier === "invisible" && !steppedUp) {
              steppedUp = true;
              controller.escalate("server_step_up", info.managedSiteKey);
              proof = await controller.getProof();
              continue;
            }

            // Token vencido o ya usado: uno fresco y otra vez, sin molestar a nadie.
            if (info?.code === "TURNSTILE_EXPIRED" && !refreshed) {
              refreshed = true;
              controller.refresh();
              proof = await controller.getProof();
              continue;
            }

            if (info) {
              const shown =
                info.code === "TURNSTILE_STEP_UP" ? { ...info, ...UNSATISFIABLE_STEP_UP } : info;
              setNotice(shown);
              if (shown.retryAfterSeconds > 0) startCountdown(shown.retryAfterSeconds);
              return { ok: false, reason: "rejected", error };
            }
            return { ok: false, reason: "error", error };
          }
        }
      } finally {
        busyRef.current = false;
        setBusy(false);
        // Los tokens son de un solo uso: intento nuevo y token fresco para el próximo envío.
        controller.reset();
      }
    },
    [buildBody, clearCountdown, controller, startCountdown],
  );

  const phoneChanged = useCallback(() => {
    const current = noticeRef.current;
    if (!current || !isPerNumberLimit(current)) return;
    noticeRef.current = null;
    // Ya, sin esperar al próximo render: un envío inmediato no puede chocar con la espera vieja.
    secondsLeftRef.current = 0;
    setNotice(null);
    clearCountdown();
  }, [clearCountdown]);

  return useMemo(
    () => ({
      surface,
      turnstile,
      trapRef: signals.trapRef,
      send,
      busy,
      verifying: turnstile.verifying,
      notice,
      retrySecondsLeft: countdown.secondsLeft,
      retryTotalSeconds: countdown.totalSeconds,
      phoneChanged,
    }),
    [
      surface,
      turnstile,
      signals.trapRef,
      send,
      busy,
      notice,
      countdown.secondsLeft,
      countdown.totalSeconds,
      phoneChanged,
    ],
  );
}
